import { BreadcrumbList, PageSection, PageSectionContent } from '@greystar/designsystem';
import { useAnalytics, useFormattedAddress } from '@greystar/lib';
import { EventProperty } from '@greystar/lib/src/types/property-types';
import { BreadcrumbLink, EditorialBreadcrumbProps } from '@greystar/ui';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

const EditorialBreadcrumb = (props: EditorialBreadcrumbProps): JSX.Element => {
  const analytics = useAnalytics();
  const { sitecoreContext } = useSitecoreContext();
  const property: EventProperty = sitecoreContext?.property ?? {};
  const location = property?.location ?? {};
  const property_location = useFormattedAddress(location);

  const homeText = 'Home';
  const includeHome = props.params?.includeHome !== 'false';
  const currentItem = props.fields?.data?.item ?? {};
  const ancestors = currentItem.ancestors ?? [];

  const validAncestors = ancestors.filter(
    (ancestor) => ancestor?.displayName && ancestor?.url?.path
  );

  const currentLabel = currentItem.displayName ?? '';
  const currentUrl = currentItem.url?.path ?? '';

  const handleBreadcrumbClick = (url: string, label: string) => {
    const breadcrumbTrail = links.map((link) => link.label).join(' > ');
    analytics.trackEvent('breadcrumbs', {
      event: 'breadcrumbs',
      action_type: `back to ${label}`,
      state: location?.state ?? '',
      property_location,
      property_id: property.id ?? '',
      property_name: property.name ?? '',
      module_name: 'breadcrumb navigation',
      inpage_location: 'top',
      click_text: breadcrumbTrail,
      click_type: 'internal',
      destination_url: url,
    });
  };

  const links: BreadcrumbLink[] = [];

  if (includeHome) {
    links.push({
      label: homeText,
      url: '/',
      onClick: () => handleBreadcrumbClick('/', homeText),
    });
  }

  const normalizedCurrentUrl = currentUrl.replace(/^\/+/, '/');
  const urlSegments = normalizedCurrentUrl
    .split('/')
    .filter((segment) => segment && !segment.includes('.'));

  const ancestorUrlMap = new Map<string, string>();
  validAncestors.forEach((ancestor) => {
    const label = ancestor.displayName!;
    let url = ancestor.url!.path!;
    url = url.replace(/^\/+/, '/');
    if (!url.includes('.')) {
      ancestorUrlMap.set(url, label);
    }
  });

  let currentPath = '';
  urlSegments.forEach((segment) => {
    currentPath += `/${segment}`;
    const normalizedPath = currentPath.replace(/^\/+/, '/');

    let label =
      ancestorUrlMap.get(normalizedPath) ??
      segment
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    if (normalizedPath === normalizedCurrentUrl && currentLabel) {
      label = currentLabel;
    }

    if (
      !links.some(
        (link) => link.url === normalizedPath || link.label.toLowerCase() === label.toLowerCase()
      )
    ) {
      links.push({
        label,
        url: normalizedPath,
        onClick: () => handleBreadcrumbClick(normalizedPath, label),
      });
    }
  });

  if (
    currentLabel &&
    normalizedCurrentUrl &&
    !links.some(
      (link) =>
        link.url === normalizedCurrentUrl || link.label.toLowerCase() === currentLabel.toLowerCase()
    )
  ) {
    links.push({
      label: currentLabel,
      url: normalizedCurrentUrl,
      onClick: () => handleBreadcrumbClick(normalizedCurrentUrl, currentLabel),
    });
  }

  if (links.length === 0) {
    console.warn('[Breadcrumb] No valid links generated');
  }

  return (
    <PageSection isBottomFlushed="true" isTopFlushed="true">
      <PageSectionContent data-component="editorial-breadcrumb">
        <BreadcrumbList breadcrumb={links} aria-label="Breadcrumb navigation" />
      </PageSectionContent>
    </PageSection>
  );
};

export default EditorialBreadcrumb;
